import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, RefreshCw, X } from 'lucide-react';

const OfferSiteWebview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  
  // Get site info from navigation state
  const { siteName, siteUrl, returnPath } = location.state || {};
  
  // If no state was passed, redirect back
  if (!siteName || !siteUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="text-center p-6">
          <p className="text-sm" style={{ color: '#6B7280' }}>No site selected</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath);
    } else {
      navigate(-1);
    }
  };

  const handleOpenExternal = () => {
    window.open(siteUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setLoadError(false);
    // Force iframe reload by updating key
    const iframe = document.getElementById('offer-site-iframe');
    if (iframe) {
      iframe.src = siteUrl;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b"
        style={{ 
          backgroundColor: '#FFFFFF', 
          borderBottomColor: '#E5E7EB' 
        }}
      >
        <div className="max-w-screen-sm mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: '#0B1221' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            
            {/* Center: Site name with brand */}
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm" style={{ color: '#001F3F' }}>
                {siteName}
              </span>
              <span className="text-xs font-medium" style={{ color: '#9CA3AF' }}>
                via
              </span>
              <span className="text-xs font-semibold">
                <span style={{ color: '#001F3F' }}>VINdicated.</span>
                <span style={{ color: '#FF4136' }}>ai</span>
              </span>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" style={{ color: '#6B7280' }} />
              </button>
              <button
                onClick={handleOpenExternal}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Open in browser"
              >
                <ExternalLink className="w-4 h-4" style={{ color: '#6B7280' }} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && !loadError && (
        <div className="absolute inset-0 top-14 flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
          <div className="text-center">
            <div 
              className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3"
              style={{ borderColor: '#E5E7EB', borderTopColor: '#001F3F' }}
            />
            <p className="text-sm" style={{ color: '#6B7280' }}>Loading {siteName}...</p>
          </div>
        </div>
      )}

      {/* Error state with fallback option */}
      {loadError && (
        <div className="flex-1 flex items-center justify-center p-6" style={{ backgroundColor: '#F9FAFB' }}>
          <div className="text-center max-w-sm">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            >
              <X className="w-6 h-6" style={{ color: '#EF4444' }} />
            </div>
            <h3 className="font-semibold mb-2" style={{ color: '#111827' }}>
              Unable to load in app
            </h3>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
              {siteName} cannot be embedded in the app. You can open it in your browser instead.
            </p>
            <div className="space-y-2">
              <button
                onClick={handleOpenExternal}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
                style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
              >
                <ExternalLink className="w-4 h-4" />
                <span>Open in Browser</span>
              </button>
              <button
                onClick={handleBack}
                className="w-full py-3 px-4 rounded-xl font-medium text-sm"
                style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iframe for web view */}
      <iframe
        id="offer-site-iframe"
        src={siteUrl}
        title={siteName}
        className="flex-1 w-full border-0"
        style={{ 
          minHeight: 'calc(100vh - 56px)',
          display: loadError ? 'none' : 'block'
        }}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setLoadError(true);
        }}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
      />

      {/* Info bar at bottom */}
      <div 
        className="sticky bottom-0 py-2 px-4 border-t text-center"
        style={{ backgroundColor: '#F9FAFB', borderTopColor: '#E5E7EB' }}
      >
        <p className="text-xs" style={{ color: '#9CA3AF' }}>
          VINdicated.ai is not affiliated with {siteName}. This is for educational purposes only.
        </p>
      </div>
    </div>
  );
};

export default OfferSiteWebview;
